<template>
  <div
    v-loading="csvDownloading"
    class="tw-benchmark"
    :element-loading-text="csvProgressText"
    element-loading-custom-class="csv-loading"
  >
    <div class="card">
      <div class="tw-benchmark__tabs-wrapper">
        <div v-if="activeTab === 'twitter_benchmark_analytics'" class="tw-benchmark__calendar">
          <AppDateRangePicker
            class="tw-benchmark__calendar-picker"
            :value="dateRange.range.value"
            :picker-options="calendarPickerOptions"
            @change="onChangeDateRange"
          />
          <el-tooltip
            placement="top"
            :content="CALENDAR_RANGE_TOOLTIP"
            popper-class="tw-benchmark-controls__calendar-tooltip"
          >
            <i class="mdi mdi-help-circle tw-benchmark__calendar-help" />
          </el-tooltip>
        </div>

        <el-tabs v-model="activeTab" class="tw-benchmark-tabs" @tab-click="handleTabClick">
          <el-tab-pane label="キャンペーン一覧" name="campaign-list" />
          <el-tab-pane label="競合キャンペーン分析" name="twitter_benchmark_analytics">
            <TwBenchmarkHeader :date-range="dateRange" :csv-can-download="csvCanDownload" :csv-download="csvDownload" />

            <div v-if="selectedPostIds.length" class="tw-benchmark__bulk-bar">
              <span>{{ selectedPostIds.length }} 件選択中</span>
              <el-button size="small" type="primary" @click="openBulkAssign">選択した投稿にタグ付与</el-button>
              <el-button size="small" @click="onClearSelection">選択解除</el-button>
            </div>

            <TwBenchmarkTable
              :selected-post-ids="selectedPostIds"
              @row-click="onRowClick"
              @toggle-select="onToggleSelect"
              @toggle-select-all="onToggleSelectAll"
              @assign-tag="onAssignTag"
            />
            <TwBenchmarkPagination class="tw-benchmark__pagination" />
            <TwBenchmarkPostDetailDrawer
              :visible="drawer.isOpen.value"
              :detail="detail"
              :loading="detailLoading"
              :current-index="drawer.globalIndex.value"
              :total="drawer.total.value"
              :is-prev="drawer.isPrev.value"
              :is-next="drawer.isNext.value"
              @close="drawer.close"
              @prev="drawer.navigatePrev"
              @next="drawer.navigateNext"
              @assign-tag="onAssignTag"
            />
            <TwBenchmarkTagAssignDialog
              :visible="tagAssignVisible"
              :mode="assignMode"
              :initial-tag-ids="assignTargetTagIds"
              :target-count="selectedPostIds.length"
              @close="tagAssignVisible = false"
              @save="onSaveTagAssign"
            />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import type { TabPane } from 'element-ui'
import { computed, defineComponent, getCurrentInstance, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import type { Route } from 'vue-router'
import type VueRouter from 'vue-router'

import { AppDateRangePicker } from '@/client/components/_atoms'
import { TrackingService } from '@/client/services'

import {
  TwBenchmarkHeader,
  TwBenchmarkTable,
  TwBenchmarkPagination,
  TwBenchmarkPostDetailDrawer,
  TwBenchmarkTagAssignDialog
} from '../components'
import { useCampaignDateRangeFilter, type DateRangeValue } from '../hooks/useCampaignDateRangeFilter'
import { useNotify } from '../hooks/useNotify'
import { usePostDetailDrawer } from '../hooks/usePostDetailDrawer'
import { MAX_SELECTABLE_POST_COUNT, useTagAssignment } from '../hooks/useTagAssignment'
import { useTwBenchmarkCsvDownload } from '../hooks/useTwBenchmarkCsvDownload'
import { useTwBenchmarkQuerySync } from '../hooks/useTwBenchmarkQuerySync'
import { useTwBenchmarkPostsStore } from '../store/useTwBenchmarkPostsStore'
import { useTwBenchmarkTableSettingsStore } from '../store/useTwBenchmarkTableSettingsStore'
import { useTwBenchmarkTagsStore } from '../store/useTwBenchmarkTagsStore'

const CALENDAR_RANGE_TOOLTIP =
  'この期間に開始したキャンペーンを表示します。\n（キャンペーンの開始日時が、選択した期間内に含まれる投稿が対象です）'

/**
 * X 競合キャンペーン分析ページの親（配線役）。mount 時に表示列設定・タグ・投稿一覧を並行取得し、各子コンポーネントを束ねる。
 */
export default defineComponent({
  name: 'TwBenchmark',

  components: {
    AppDateRangePicker,
    TwBenchmarkHeader,
    TwBenchmarkTable,
    TwBenchmarkPagination,
    TwBenchmarkPostDetailDrawer,
    TwBenchmarkTagAssignDialog
  },

  setup() {
    const postsStore = useTwBenchmarkPostsStore()
    const settingsStore = useTwBenchmarkTableSettingsStore()
    const tagsStore = useTwBenchmarkTagsStore()
    const instanceProxy = getCurrentInstance()?.proxy as { $router: VueRouter; $route: Route } | undefined

    const querySync = useTwBenchmarkQuerySync({ router: instanceProxy?.$router, route: instanceProxy?.$route })

    const drawer = usePostDetailDrawer({ router: instanceProxy?.$router, loadPage: querySync.loadPage })
    const { selectedPostIds, toggleSelect, toggleSelectAll, clearSelection, assignSingle, assignBulk } =
      useTagAssignment()
    const { handleCustomErrorNotify } = useNotify()

    const dateRange = useCampaignDateRangeFilter()
    const now = () => new Date()
    const calendarPickerOptions = computed(() => ({
      popperClass: 'tw-benchmark-controls__calendar-panel',
      shortcuts: dateRange.presets.map(preset => ({
        text: preset.label,
        onClick(picker: { minDate: Date; maxDate: Date; $emit: (e: string, v: [Date, Date]) => void }) {
          const [start, end] = preset.range()
          const startDate = new Date(start)
          const endDate = new Date(end)
          picker.minDate = startDate
          picker.maxDate = endDate
          picker.$emit('pick', [startDate, endDate])
        }
      })),
      disabledDate(date: Date) {
        return date.getTime() > now().getTime()
      }
    }))
    const onChangeDateRange = (value: DateRangeValue): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン開始期間')
      dateRange.setRange(value)
      void postsStore.setDateRange({ startDatetime: value[0], endDatetime: value[1] })
    }

    const {
      canDownload: csvCanDownload,
      isDownloading: csvDownloading,
      progressText: csvProgressText,
      download: csvDownload
    } = useTwBenchmarkCsvDownload()

    const notifySelectionLimit = (): void => {
      handleCustomErrorNotify(`${MAX_SELECTABLE_POST_COUNT} 件以上更新できません。`)
    }

    const router = instanceProxy?.$router

    const activeTab = ref<'campaign-list' | 'twitter_benchmark_analytics'>('twitter_benchmark_analytics')

    const tagAssignVisible = ref(false)
    const assignMode = ref<'single' | 'bulk'>('single')
    const assignTargetPostId = ref<number | null>(null)
    const assignTargetTagIds = ref<number[]>([])

    const handleTabClick = (tab: TabPane): void => {
      if (tab.name === 'campaign-list') {
        void router?.push('/campaigns')
      }
    }

    const onRowClick = (postId: number): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細を開く')
      void drawer.open(postId)
    }

    const onAssignTag = (postId: number): void => {
      assignMode.value = 'single'
      assignTargetPostId.value = postId
      const rowTags = postsStore.posts.find(row => row.post.id === postId)?.tags
      assignTargetTagIds.value = (rowTags ?? postsStore.detail?.tags ?? []).map(tag => tag.id)
      tagAssignVisible.value = true
    }

    const onToggleSelect = (postId: number): void => {
      const isAccepted = toggleSelect(postId)
      if (!isAccepted) {
        notifySelectionLimit()
      }
    }

    const onToggleSelectAll = (): void => {
      const isWithinLimit = toggleSelectAll(postsStore.posts.map(row => row.post.id))
      if (!isWithinLimit) {
        notifySelectionLimit()
      }
    }

    const openBulkAssign = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|選択した投稿にタグ付与')
      assignMode.value = 'bulk'
      assignTargetTagIds.value = []
      tagAssignVisible.value = true
    }

    const onClearSelection = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|選択解除')
      clearSelection()
    }

    const onSaveTagAssign = async ({ tagIds }: { tagIds: number[] }): Promise<void> => {
      if (assignMode.value === 'single') {
        if (assignTargetPostId.value === null) return
        await assignSingle(assignTargetPostId.value, tagIds)
      } else {
        const targetPostIds = [...selectedPostIds.value]
        await assignBulk(tagIds)
        clearSelection()
        if (
          drawer.isOpen.value &&
          drawer.currentPostId.value !== null &&
          targetPostIds.includes(drawer.currentPostId.value)
        ) {
          await postsStore.fetchPostDetail(drawer.currentPostId.value)
        }
        return
      }

      if (drawer.isOpen.value && drawer.currentPostId.value === assignTargetPostId.value) {
        await postsStore.fetchPostDetail(assignTargetPostId.value)
      }
    }

    const parseRoutePostId = (route: Route | undefined): number | null => {
      const raw = route?.params.postId
      if (raw === undefined || raw === '') return null
      const parsed = Number(raw)
      return Number.isInteger(parsed) ? parsed : null
    }

    onMounted(async () => {
      await Promise.all([settingsStore.load(), tagsStore.fetchTags()])

      await querySync.start()

      watch(
        () => [
          postsStore.searchFilters,
          postsStore.sortBy,
          postsStore.order,
          postsStore.currentPage,
          postsStore.pageSize
        ],
        () => {
          querySync.syncUrlFromStore()
        },
        { deep: true }
      )

      await drawer.syncFromRoute(parseRoutePostId(instanceProxy?.$route))

      watch(
        () => instanceProxy?.$route.params.postId,
        () => {
          void drawer.syncFromRoute(parseRoutePostId(instanceProxy?.$route))
        }
      )
    })

    onBeforeUnmount(() => {
      querySync.stop()
    })

    return {
      activeTab,
      handleTabClick,
      drawer,
      dateRange,
      calendarPickerOptions,
      onChangeDateRange,
      CALENDAR_RANGE_TOOLTIP,
      detail: computed(() => postsStore.detail),
      detailLoading: computed(() => postsStore.detailLoading),
      selectedPostIds,
      onToggleSelect,
      onToggleSelectAll,
      onClearSelection,
      tagAssignVisible,
      assignMode,
      assignTargetTagIds,
      onRowClick,
      onAssignTag,
      openBulkAssign,
      onSaveTagAssign,
      csvCanDownload,
      csvDownloading,
      csvProgressText,
      csvDownload
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

$tw-benchmark-tabs-header-height: 40px;

.tw-benchmark {
  .page-title {
    margin-bottom: 16px;
  }

  &__tabs-wrapper {
    position: relative;
  }

  &__calendar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: flex;
    gap: $spacing-unit;
    align-items: center;
    height: $tw-benchmark-tabs-header-height;
  }

  &__calendar-picker {
    :deep(.el-input__icon) {
      color: $text-color;
    }
  }

  &__calendar-help {
    font-size: 20px;
    line-height: 1;
    color: $sub-text-color;
    cursor: help;
  }

  :deep(.tw-benchmark-tabs) {
    .el-tabs__nav-wrap {
      display: inline-block;
    }
  }

  &__pagination {
    margin-top: 16px;
  }

  &__bulk-bar {
    display: flex;
    gap: $spacing-unit * 1.5;
    align-items: center;
    padding: $spacing-unit $spacing-unit * 1.5;
    margin-bottom: $spacing-unit;
    background-color: rgba($color: $primary-color, $alpha: 8%);
    border-radius: $round;
  }
}
</style>

<style lang="scss">
.tw-benchmark {
  .csv-loading {
    .el-loading-spinner {
      top: 40vh;

      .el-loading-text {
        white-space: pre-line;
      }
    }
  }
}

.tw-benchmark-controls__calendar-tooltip {
  white-space: pre-line;
}

.tw-benchmark-controls__calendar-panel {
  .el-picker-panel__footer {
    padding: 8px;
  }
}
</style>
